import { cn } from "@/lib/utils/cn";
import { ChevronDown } from "lucide-react";
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

type ScrollHintProps = {
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  fadeClassName?: string;
  hintClassName?: string;
  icon?: ReactNode;
  showOnTop?: boolean;
  scrollContainerRef?: RefObject<HTMLDivElement>;
};

export function ScrollHint({
  children,
  className,
  contentClassName,
  fadeClassName,
  hintClassName,
  icon,
  showOnTop = false,
  scrollContainerRef,
}: ScrollHintProps) {
  const internalScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = scrollContainerRef ?? internalScrollRef;
  const [showTopHint, setShowTopHint] = useState(false);
  const [showBottomHint, setShowBottomHint] = useState(false);

  const updateScrollHint = useCallback(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) {
      return;
    }

    const { scrollHeight, clientHeight, scrollTop } = scrollElement;
    const hasOverflow = scrollHeight > clientHeight + 1;
    const isAtTop = scrollTop <= 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 4;

    setShowTopHint(showOnTop && hasOverflow && !isAtTop);
    setShowBottomHint(hasOverflow && !isAtBottom);
  }, [scrollRef, showOnTop]);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) {
      return;
    }

    updateScrollHint();
    scrollElement.addEventListener("scroll", updateScrollHint, {
      passive: true,
    });
    window.addEventListener("resize", updateScrollHint);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateScrollHint);
      resizeObserver.observe(scrollElement);
      if (scrollElement.firstElementChild) {
        resizeObserver.observe(scrollElement.firstElementChild);
      }
    }

    return () => {
      scrollElement.removeEventListener("scroll", updateScrollHint);
      window.removeEventListener("resize", updateScrollHint);
      resizeObserver?.disconnect();
    };
  }, [scrollRef, updateScrollHint]);

  return (
    <div className={cn("relative min-h-0", className)}>
      <div
        ref={scrollRef}
        className={cn("h-full min-h-0 overflow-y-auto", contentClassName)}
      >
        {children}
      </div>
      {showTopHint ? (
        <div className="pointer-events-none absolute inset-x-0 top-0">
          <div
            className={cn(
              "from-background/70 h-8 bg-gradient-to-b to-transparent",
              fadeClassName,
            )}
          />
        </div>
      ) : null}
      {showBottomHint ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-0">
          <div
            className={cn(
              "from-background/70 h-8 bg-gradient-to-t to-transparent",
              fadeClassName,
            )}
          />
          <div
            className={cn(
              "text-muted-foreground absolute inset-x-0 bottom-2 flex items-center justify-center",
              hintClassName,
            )}
          >
            {icon ?? <ChevronDown className="h-5 w-5" aria-hidden="true" />}
          </div>
        </div>
      ) : null}
    </div>
  );
}
